import { Controller } from '@hotwired/stimulus'
import consumer from '~/channels/consumer'

/**
 * Deployment Status Controller
 *
 * Handles Cloudflare deployment via AJAX with real-time progress updates.
 * - No page reloads during deployment
 * - Toast notifications instead of flash messages
 * - Smooth progress animations
 */
export default class extends Controller {
  static targets = [
    'badge',
    'deployButton',
    'deployIcon',
    'deploySpinner',
    'deployText',
    'progress',
    'progressContainer',
    'progressBar',
    'progressMessage',
    'progressStatus',
    'spinner',
    'lastDeployed',
    'version',
    'imagesSynced',
    'previewUrl',
    'previewUrlContainer',
    'previewUrlInput'
  ]

  static values = {
    websiteId: String,
    statusUrl: String
  }

  connect() {
    console.log('[DeploymentStatus] Connected for website:', this.websiteIdValue)
    this.subscribeToChannel()

    // If already deploying on page load, start polling
    if (this.hasProgressTarget && this.progressTarget.classList.contains('opacity-100')) {
      this.isDeploying = true
      this.startPolling()
    }
  }

  disconnect() {
    this.stopPolling()
    this.unsubscribeFromChannel()
  }

  // ==================== ActionCable ====================

  subscribeToChannel() {
    this.subscription = consumer.subscriptions.create(
      { channel: 'DeploymentProgressChannel', website_id: this.websiteIdValue },
      {
        connected: () => console.log('[DeploymentStatus] ActionCable connected'),
        disconnected: () => console.log('[DeploymentStatus] ActionCable disconnected'),
        received: (data) => this.handleBroadcast(data)
      }
    )
  }

  unsubscribeFromChannel() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  handleBroadcast(data) {
    console.log('[DeploymentStatus] Broadcast:', data)

    switch (data.event) {
      case 'deployment_validating':
        this.setProgress('Validating website...', 10)
        break
      case 'deployment_syncing':
        this.setProgress(data.message || 'Syncing images to R2...', data.percent || 25)
        break
      case 'deployment_creating_project':
        this.setProgress('Creating Cloudflare project...', 40)
        break
      case 'deployment_uploading':
        this.setProgress(data.message || 'Uploading to Cloudflare...', 60)
        break
      case 'deployment_activating':
        this.setProgress('Activating deployment...', 85)
        break
      case 'deployment_completed':
        this.handleComplete(data)
        break
      case 'deployment_failed':
        this.handleFailed(data)
        break
      default:
        if (data.message) {
          this.setProgress(data.message, data.percent || 50)
        }
    }
  }

  // ==================== Deploy Actions ====================

  async deploy(event) {
    event.preventDefault()
    await this.startDeployment(false)
  }

  async deployWithSync(event) {
    event.preventDefault()
    await this.startDeployment(true)
  }

  async startDeployment(forceSync = false) {
    if (this.isDeploying) return

    this.isDeploying = true
    this.showDeployingState()

    try {
      const url = `/admin/websites/${this.websiteIdValue}/deploy`
      const params = new URLSearchParams()
      if (forceSync) params.append('force_image_sync', 'true')

      const response = await fetch(`${url}?${params}`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this.showToast('Deployment started', 'info')
        this.startPolling()
      } else {
        this.handleFailed({ message: data.error || 'Failed to start deployment' })
      }
    } catch (error) {
      console.error('[DeploymentStatus] Deploy error:', error)
      this.handleFailed({ message: 'Network error. Please try again.' })
    }
  }

  // ==================== UI State ====================

  showDeployingState() {
    // Update button
    if (this.hasDeployButtonTarget) {
      this.deployButtonTarget.disabled = true
    }
    if (this.hasDeployIconTarget) {
      this.deployIconTarget.classList.add('hidden')
    }
    if (this.hasDeploySpinnerTarget) {
      this.deploySpinnerTarget.classList.remove('hidden')
    }
    if (this.hasDeployTextTarget) {
      this.deployTextTarget.textContent = 'Deploying...'
    }

    // Update badge
    this.setBadge('Deploying...', 'badge-warning')

    // Show progress
    this.showProgress()
    this.setProgress('Starting deployment...', 5)
  }

  showProgress() {
    if (this.hasProgressTarget) {
      this.progressTarget.classList.remove('opacity-0', 'h-0', 'overflow-hidden')
      this.progressTarget.classList.add('opacity-100')
    }
  }

  hideProgress() {
    if (this.hasProgressTarget) {
      this.progressTarget.classList.add('opacity-0', 'h-0', 'overflow-hidden')
      this.progressTarget.classList.remove('opacity-100')
    }
  }

  setProgress(message, percent) {
    this.showProgress()

    if (this.hasProgressMessageTarget) {
      this.progressMessageTarget.textContent = message
    }

    if (this.hasProgressBarTarget && percent !== undefined) {
      this.progressBarTarget.style.width = `${Math.min(percent, 100)}%`
    }
  }

  setBadge(text, badgeClass) {
    if (this.hasBadgeTarget) {
      this.badgeTarget.textContent = text
      this.badgeTarget.className = `badge ${badgeClass}`
    }
  }

  // ==================== Completion Handlers ====================

  handleComplete(data) {
    this.isDeploying = false
    this.stopPolling()

    // Update progress to success state
    this.setProgress('Deployment complete!', 100)
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.classList.remove('progress-bar-primary', 'progress-bar-striped', 'progress-bar-animated')
      this.progressBarTarget.classList.add('progress-bar-success')
    }

    // Update badge
    this.setBadge('Deployed', 'badge-success')

    // Reset button and re-enable it
    this.resetButton('Deploy to Cloudflare')
    if (this.hasDeployButtonTarget) {
      this.deployButtonTarget.disabled = false
    }

    // Update stats
    if (this.hasLastDeployedTarget) {
      this.lastDeployedTarget.textContent = 'Just now'
    }
    if (this.hasVersionTarget && data.version) {
      this.versionTarget.textContent = `v${data.version}`
    }

    // Update preview URL if available
    if (data.preview_url) {
      if (this.hasPreviewUrlTarget) {
        this.previewUrlTarget.href = data.preview_url
      }
      if (this.hasPreviewUrlInputTarget) {
        this.previewUrlInputTarget.value = data.preview_url
      }
      if (this.hasPreviewUrlContainerTarget) {
        this.previewUrlContainerTarget.classList.remove('hidden')
      }
    }

    // Show success toast with preview link
    if (data.preview_url) {
      this.showToastWithLink('Deployment complete!', data.preview_url, 'success')
    } else {
      this.showToast('Deployment complete!', 'success')
    }

    // Hide progress after a moment
    setTimeout(() => {
      this.hideProgress()
      // Reset progress bar for next deployment
      if (this.hasProgressBarTarget) {
        this.progressBarTarget.style.width = '0%'
        this.progressBarTarget.classList.add('progress-bar-primary', 'progress-bar-striped', 'progress-bar-animated')
        this.progressBarTarget.classList.remove('progress-bar-success')
      }
    }, 2000)
  }

  handleFailed(data) {
    this.isDeploying = false
    this.stopPolling()

    // Update progress to error state
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.classList.remove('progress-bar-primary', 'progress-bar-striped', 'progress-bar-animated')
      this.progressBarTarget.classList.add('progress-bar-error')
    }
    if (this.hasProgressMessageTarget) {
      this.progressMessageTarget.textContent = data.message || 'Deployment failed'
    }

    // Update badge
    this.setBadge('Failed', 'badge-error')

    // Reset button to allow retry
    this.resetButton('Retry Deployment')
    if (this.hasDeployButtonTarget) {
      this.deployButtonTarget.disabled = false
    }

    // Show error toast
    this.showToast(data.message || 'Deployment failed', 'error')

    // Hide progress after delay
    setTimeout(() => {
      this.hideProgress()
      if (this.hasProgressBarTarget) {
        this.progressBarTarget.style.width = '0%'
        this.progressBarTarget.classList.add('progress-bar-primary', 'progress-bar-striped', 'progress-bar-animated')
        this.progressBarTarget.classList.remove('progress-bar-error')
      }
    }, 3000)
  }

  resetButton(text) {
    if (this.hasDeploySpinnerTarget) {
      this.deploySpinnerTarget.classList.add('hidden')
    }
    if (this.hasDeployIconTarget) {
      this.deployIconTarget.classList.remove('hidden')
    }
    if (this.hasDeployTextTarget) {
      this.deployTextTarget.textContent = text
    }
  }

  // ==================== Polling Fallback ====================

  startPolling() {
    if (this.pollInterval) return
    console.log('[DeploymentStatus] Starting polling')
    this.pollInterval = setInterval(() => this.pollStatus(), 3000)
  }

  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
      this.pollInterval = null
    }
  }

  async pollStatus() {
    try {
      const response = await fetch(this.statusUrlValue, {
        headers: { 'Accept': 'application/json' }
      })

      if (!response.ok) return

      const data = await response.json()

      // Update badge based on status
      const statusMap = {
        'pending': { label: 'Pending', badge: 'badge-default' },
        'uploading': { label: 'Deploying...', badge: 'badge-warning' },
        'deployed': { label: 'Deployed', badge: 'badge-success' },
        'failed': { label: 'Failed', badge: 'badge-error' }
      }

      const status = statusMap[data.cf_deployment_status]
      if (status) {
        this.setBadge(status.label, status.badge)
      }

      // Handle completion states
      if (data.cf_deployment_status === 'deployed') {
        this.handleComplete({ version: data.deployment_version })
      } else if (data.cf_deployment_status === 'failed') {
        this.handleFailed({ message: data.cf_deployment_error || 'Deployment failed' })
      } else if (data.cf_deployment_status === 'uploading') {
        // Increment progress while uploading
        const currentWidth = parseInt(this.progressBarTarget?.style.width, 10) || 0
        if (currentWidth < 85) {
          this.setProgress('Uploading to Cloudflare...', currentWidth + 5)
        }
      }
    } catch (error) {
      console.error('[DeploymentStatus] Poll error:', error)
    }
  }

  // ==================== Toast Notifications ====================

  showToast(message, type = 'info') {
    // Remove existing toasts
    document.querySelectorAll('.deployment-toast').forEach((t) => t.remove())

    const toast = document.createElement('div')
    toast.className = `deployment-toast deployment-toast-${type}`

    const icons = {
      success: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>',
      error: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>',
      info: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>'
    }

    toast.innerHTML = `
      <div class="deployment-toast-content">
        ${icons[type] || icons.info}
        <span>${message}</span>
      </div>
    `

    this.ensureToastStyles()
    document.body.appendChild(toast)

    // Animate in
    requestAnimationFrame(() => {
      toast.classList.add('deployment-toast-visible')
    })

    // Auto dismiss
    setTimeout(() => {
      toast.classList.remove('deployment-toast-visible')
      setTimeout(() => toast.remove(), 300)
    }, 4000)
  }

  showToastWithLink(message, url, type = 'info') {
    // Remove existing toasts
    document.querySelectorAll('.deployment-toast').forEach((t) => t.remove())

    const toast = document.createElement('div')
    toast.className = `deployment-toast deployment-toast-${type}`

    const icons = {
      success: '<svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>',
      error: '<svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>',
      info: '<svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>'
    }

    toast.innerHTML = `
      <div class="deployment-toast-content deployment-toast-with-link">
        ${icons[type] || icons.info}
        <div class="deployment-toast-text">
          <span class="deployment-toast-message">${message}</span>
          <a href="${url}" target="_blank" rel="noopener noreferrer" class="deployment-toast-link">
            ${url}
            <svg class="w-3 h-3 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
            </svg>
          </a>
        </div>
      </div>
    `

    this.ensureToastStyles()
    document.body.appendChild(toast)

    // Animate in
    requestAnimationFrame(() => {
      toast.classList.add('deployment-toast-visible')
    })

    // Auto dismiss after longer time for link toast
    setTimeout(() => {
      toast.classList.remove('deployment-toast-visible')
      setTimeout(() => toast.remove(), 300)
    }, 8000)
  }

  // ==================== Copy Actions ====================

  async copyUrl(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.url
    if (!url) return

    await this.copyToClipboard(url, 'URL copied!')
  }

  async copyError(event) {
    event.preventDefault()
    const error = event.currentTarget.dataset.error
    if (!error) return

    await this.copyToClipboard(error, 'Error copied!')
  }

  async copyDns(event) {
    event.preventDefault()
    const dns = event.currentTarget.dataset.dns
    if (!dns) return

    await this.copyToClipboard(dns, 'DNS record copied!')
  }

  async copyToClipboard(text, successMessage = 'Copied!') {
    try {
      await navigator.clipboard.writeText(text)
      this.showToast(successMessage, 'success')
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()

      try {
        document.execCommand('copy')
        this.showToast(successMessage, 'success')
      } catch (err) {
        console.error('[DeploymentStatus] Copy failed:', err)
        this.showToast('Failed to copy', 'error')
      }

      document.body.removeChild(textArea)
    }
  }

  ensureToastStyles() {
    if (document.getElementById('deployment-toast-styles')) return

    const style = document.createElement('style')
    style.id = 'deployment-toast-styles'
    style.textContent = `
      .deployment-toast {
        position: fixed;
        bottom: 24px;
        right: 24px;
        z-index: 10000;
        transform: translateY(16px);
        opacity: 0;
        transition: transform 0.3s ease, opacity 0.3s ease;
        pointer-events: none;
      }
      .deployment-toast-visible {
        transform: translateY(0);
        opacity: 1;
        pointer-events: auto;
      }
      .deployment-toast-content {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 20px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05);
      }
      .deployment-toast-success .deployment-toast-content {
        background: #ecfdf5;
        color: #065f46;
      }
      .deployment-toast-error .deployment-toast-content {
        background: #fef2f2;
        color: #991b1b;
      }
      .deployment-toast-info .deployment-toast-content {
        background: #eff6ff;
        color: #1e40af;
      }
      .deployment-toast-with-link {
        flex-direction: row;
        align-items: flex-start;
        max-width: 400px;
      }
      .deployment-toast-text {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
      .deployment-toast-message {
        font-weight: 600;
      }
      .deployment-toast-link {
        font-size: 12px;
        font-weight: 500;
        text-decoration: underline;
        opacity: 0.9;
        word-break: break-all;
      }
      .deployment-toast-link:hover {
        opacity: 1;
      }
    `
    document.head.appendChild(style)
  }
}
